import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Button, { ButtonType } from 'inquiry/components/Button';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import Select from 'verify/components/Select';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedTitle from 'verify/components/ThemedTitle';
import { getAllowedCountriesList } from 'verify/lib/verification';
import { Template } from 'verify/models/template';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const CountrySelector = styled('div')`
  margin-bottom: ${SPACINGS.base * 2}px;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

interface VerifySelectCountryViewProps {
  countryCode: string;
  handleContinue: () => void;
  handleCountryChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  isProcessing: boolean;
  template: Template;
}

export default function VerifySelectCountryView(props: VerifySelectCountryViewProps) {
  const { countryCode, handleContinue, isProcessing, template } = props;
  const { 'enabled-country-codes': allowedCountryCodes } = template.attributes;

  const [t] = useTranslation();

  return (
    <div>
      <Header>
        <ThemedTitle>{t('select-country.title')}</ThemedTitle>
        <ThemedParagraph>{t('select-country.prompt')}</ThemedParagraph>
      </Header>

      <CountrySelector>
        <Select
          data-test='country-selector__select'
          value={countryCode ?? ''}
          onChange={props.handleCountryChange}
        >
          <option value='' disabled>
            {t('select-country.placeholder')}
          </option>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {getAllowedCountriesList(allowedCountryCodes)
            //eslint-disable-next-line @persona-id/t-string-only
            .map((code) => [t(`countries.${code}`), code])
            // Sort list by translated names
            .sort((a, b) => a[0].toLowerCase().localeCompare(b[0].toLowerCase()))
            .map(([countryName, code]) => (
              <option key={code} value={code}>
                {countryName}
              </option>
            ))}
        </Select>
      </CountrySelector>

      <PositionableButtonContainer>
        <Button
          id='select-country__button--select'
          buttonType={ButtonType.Primary}
          onClick={handleContinue}
          disabled={isProcessing || !countryCode}
          isPending={isProcessing}
        >
          {t('select-country.btn-submit')}
        </Button>
      </PositionableButtonContainer>
    </div>
  );
}
